import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import type { ActiveSentinel } from 'features/timeSeries/hooks/useSentinelScores'
import useSWR from 'swr'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

/** The case summary fields the chart bubble needs. */
interface CaseSummary {
  status?: string
  target?: string
  breach_ts?: string
  actual?: number | null
  magnitude?: number | null
  origin?: string
  in_band?: boolean
}

interface CaseRow {
  case_id: string
  summary?: CaseSummary
  diagnosis?: unknown
}

/** One detection, positioned for a chart bubble on its sentinel's target pen.
    Sourced from a legacy sentinel case OR a `time-series-alert` emission —
    `caseId` is empty for an alert not (yet) attached to a case. */
export interface SentinelCaseMarker {
  caseId: string
  /** Set when the marker comes from an emitted alert rather than a case. */
  alertId?: string
  sentinelEntryId: string
  /** Datapoint id of the target pen the bubble sits on. */
  targetSensorId: string
  /** Epoch-ms of the breach. */
  breachMs: number
  /** The actual value at the breach (the bubble's y). */
  value: number
  /** 'open' | 'diagnosed' | … — drives the bubble color. */
  status: string
  /** How the case was opened: 'monitor' | 'manual'. */
  origin: string
  /** True when a manual case was forced inside the band (a bookmark). */
  inBand: boolean
  /** True once RCA has produced a diagnosis. */
  diagnosed: boolean
  /** Alert/case display title, for the bubble hover. */
  title?: string
  /** Breach magnitude in band multiples, for the bubble hover. */
  magnitude?: number | null
}

interface AlertEntryRow {
  entry?: {
    id?: string
    data?: {
      timestamp?: string
      title?: string
      magnitude?: number | null
      alerted_value?: number | null
      case_id?: string | null
      sentinel_origin?: string
      sentinel_in_band?: boolean
      sentinel_target?: string
      primary_datapoint_id?: string
      datapoint_ids?: string[]
    }
  }
}

const ALERTS_PER_SENTINEL = 200

const alertMarkersFor = (
  rows: AlertEntryRow[],
  entryId: string,
  target: string
): SentinelCaseMarker[] => {
  const markers: SentinelCaseMarker[] = []
  for (const row of rows) {
    const data = row.entry?.data
    const id = row.entry?.id
    if (!data || !id) continue
    const breachMs = data.timestamp ? new Date(data.timestamp).getTime() : NaN
    const value = data.alerted_value
    if (!Number.isFinite(breachMs) || value === null || value === undefined) {
      continue
    }
    markers.push({
      caseId: data.case_id ?? '',
      alertId: id,
      sentinelEntryId: entryId,
      targetSensorId:
        data.sentinel_target ||
        data.primary_datapoint_id ||
        data.datapoint_ids?.[0] ||
        target,
      breachMs,
      value,
      status: 'open',
      origin: data.sentinel_origin ?? 'monitor',
      inBand: data.sentinel_in_band ?? false,
      diagnosed: false,
      title: data.title,
      magnitude: data.magnitude,
    })
  }
  return markers
}

/**
 * Every case opened for the sentinels currently on the chart, positioned for a
 * bubble at (breach time, actual value) on the sentinel's target pen.
 *
 * Gated on gecko-internal exactly like {@link useSentinelScores} — the one
 * choke point — so a restored chart carrying sentinels never fetches cases for
 * someone without access. SWR keys on (site, entry ids) and each sentinel's
 * cases load independently, so one failing sentinel does not blank the rest.
 */
export const useSentinelCases = (
  siteId: string,
  activeSentinels: ActiveSentinel[],
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { FIM_SERVICE_URL, ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { hasPermissions } = useGeckoPermissions()

  const entries = activeSentinels
    .filter((sentinel) => sentinel.target)
    .map((sentinel) => ({ entryId: sentinel.entryId, target: sentinel.target }))

  const shouldFetch =
    enabled &&
    isAuthenticated &&
    hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER]) &&
    siteId !== '' &&
    entries.length > 0

  const { data, error, isLoading, mutate } = useSWR<SentinelCaseMarker[]>(
    shouldFetch
      ? [
          'sentinel-cases',
          FIM_SERVICE_URL,
          siteId,
          entries.map((entry) => entry.entryId).join(','),
        ]
      : null,
    async () => {
      const base = `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteId)}/sentinel`
      const perSentinelAlerts = await Promise.all(
        entries.map(async ({ entryId, target }) => {
          try {
            const res = await authorizedAxios.post<AlertEntryRow[]>(
              `${ROLODEX_URL}/api/v2/entries/load`,
              {
                type: ['time-series-alert'],
                tags: { site: siteId, subsystem: entryId },
                order_by: [{ field: 'created_at', descending: true }],
                limit: ALERTS_PER_SENTINEL,
              }
            )
            return alertMarkersFor(res.data ?? [], entryId, target)
          } catch {
            return [] as SentinelCaseMarker[]
          }
        })
      )
      const perSentinel = await Promise.all(
        entries.map(async ({ entryId, target }) => {
          try {
            const res = await authorizedAxios.get<{ cases: CaseRow[] }>(
              `${base}/${encodeURIComponent(entryId)}/cases`
            )
            const markers: SentinelCaseMarker[] = []
            for (const row of res.data.cases ?? []) {
              const summary = row.summary ?? {}
              const breachMs = summary.breach_ts
                ? new Date(summary.breach_ts).getTime()
                : NaN
              const value = summary.actual
              // A bubble needs a time and a y — skip cases missing either.
              if (
                !Number.isFinite(breachMs) ||
                value === null ||
                value === undefined
              ) {
                continue
              }
              markers.push({
                caseId: row.case_id,
                sentinelEntryId: entryId,
                targetSensorId: summary.target || target,
                breachMs,
                value,
                status: summary.status ?? 'open',
                origin: summary.origin ?? 'monitor',
                inBand: summary.in_band ?? false,
                diagnosed: !!row.diagnosis || summary.status === 'diagnosed',
              })
            }
            return markers
          } catch {
            return [] as SentinelCaseMarker[]
          }
        })
      )
      const caseMarkers = perSentinel.flat()
      const caseKeys = new Set(
        caseMarkers.map(
          (marker) => `${marker.sentinelEntryId}|${marker.breachMs}`
        )
      )
      const alertMarkers = perSentinelAlerts
        .flat()
        .filter(
          (marker) =>
            !caseKeys.has(`${marker.sentinelEntryId}|${marker.breachMs}`)
        )
      return [...caseMarkers, ...alertMarkers]
    },
    { keepPreviousData: true, revalidateOnFocus: false }
  )

  return { cases: data ?? EMPTY_CASES, error, isLoading, mutate }
}

const EMPTY_CASES: SentinelCaseMarker[] = []
